import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function PlanEndModal({ isOpen, mode, saving, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (isOpen) setComment('');
  }, [isOpen, mode]);

  const trimmedComment = useMemo(() => String(comment || '').trim(), [comment]);
  const isCompletedMode = mode === 'completed';

  if (!isOpen || !mode) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 p-5">
          <div className="text-lg font-extrabold text-gray-900">
            {isCompletedMode ? t('plan.end.completed_title') : t('plan.end.stopped_title')}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!saving}
            className="font-bold text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-900">
            {isCompletedMode ? t('plan.end.completed_intro') : t('plan.end.stopped_intro')}
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-gray-800" htmlFor="plan-end-comment">
              {t('plan.end.comment_label')}
            </label>
            <textarea
              id="plan-end-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={isCompletedMode ? t('plan.end.completed_placeholder') : t('plan.end.stopped_placeholder')}
              className="min-h-40 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 outline-none transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100"
              disabled={!!saving}
              autoFocus
            />
            <div className="mt-2 text-xs font-medium text-gray-500">{t('plan.end.comment_hint')}</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={!!saving}
            className="rounded-lg px-4 py-2 font-bold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            {t('workout.cancel')}
          </button>
          <button
            type="button"
            disabled={!trimmedComment || !!saving}
            onClick={() => onConfirm?.(trimmedComment)}
            className={[
              'rounded-lg px-4 py-2 font-extrabold text-white transition',
              !trimmedComment || saving
                ? 'cursor-not-allowed bg-indigo-300'
                : isCompletedMode
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-amber-600 hover:bg-amber-700'
            ].join(' ')}
          >
            {saving ? t('plan.end.submitting') : isCompletedMode ? t('plan.end.completed_confirm') : t('plan.end.stopped_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
